import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance for direct use
const api = axios.create({
  baseURL: API,
  // Default 20s timeout so any request that hangs (e.g. Cloudflare 520 during
  // pod cold-start) fails fast and the UI can show a retry instead of an
  // infinite spinner. Long-running endpoints override per-call.
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Bearer token on every request when present. Isolated instance
// wouldn't otherwise inherit the global interceptor set in /lib/auth.js.
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('academy_token');
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* ignore */ }
  return config;
});

// Auto-clear session on 401 from protected endpoints (same rule as auth.js)
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url    = error?.config?.url || '';
    const isAuthCall = /\/(auth\/login|auth\/signup|orientation-portal\/)/.test(url);
    if (status === 401 && !isAuthCall) {
      try {
        localStorage.removeItem('academy_token');
        localStorage.removeItem('academy_user');
      } catch { /* ignore */ }
      if (error?.response?.data?.blocked && !window.location.pathname.startsWith('/academy/login')) {
        window.location.href = '/academy/login?blocked=1';
      }
    }
    return Promise.reject(error);
  },
);

export default api;

// Shopify Products API
export const fetchProducts = async () => {
  try {
    const response = await axios.get(`${API}/shopify/products`);
    return response.data.products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const fetchProductByHandle = async (handle) => {
  try {
    const response = await axios.get(`${API}/shopify/products/${handle}`);
    return response.data.product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

export const fetchCollections = async () => {
  try {
    const response = await axios.get(`${API}/shopify/collections`);
    return response.data.collections;
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

// Newsletter API
export const subscribeNewsletter = async (email) => {
  try {
    const response = await axios.post(`${API}/newsletter/subscribe`, { email });
    return response.data;
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error;
  }
};

// Contact Form API
export const submitContactForm = async (formData) => {
  try {
    const response = await axios.post(`${API}/contact`, formData);
    return response.data;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};
